import * as Sentry from '@sentry/react';

/**
 * Diagnostics around full-page reloads in the learner player.
 *
 * The player triggers `window.location.reload()` in a few places (anti-speedhack
 * guard, locale mismatch, missing chapterSequenceId). When something goes wrong
 * these can fire in a loop and the learner perceives the slide as "restarting".
 *
 * This module:
 *  - reports the anti-speedhack reload to Sentry once per sequence/slide (so the
 *    reload loop on a stuck slide does not flood Sentry, while a different slide
 *    hitting the bug is still reported) and flushes the event before reloading;
 *  - records which reloads the app itself initiated, so that on the next boot we
 *    can detect reloads we did NOT initiate (e.g. a host LMS reloading our iframe)
 *    and report those too.
 */

// Reported-flags are keyed per sequence (current pathname) rather than per whole
// session: the reload loop keeps the same URL (so we still report only once per
// stuck slide), but a different slide hitting the bug is reported again.
const SPEED_REPORTED_PREFIX = 'el.speedHackReported:';
const EXTERNAL_RELOAD_REPORTED_PREFIX = 'el.externalReloadReported:';
const APP_RELOAD_REASON_KEY = 'el.appReloadReason';

// Guards against several media players detecting the same hack within one page
// load and reloading/reporting multiple times.
let handlingSpeedHack = false;

function sessionGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // sessionStorage can throw in private mode / sandboxed iframes
    return null;
  }
}

function sessionSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore — storage unavailable
  }
}

function sessionRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws — which itself means we are framed
    return true;
  }
}

/**
 * Identifies the current slide for per-sequence dedupe. The slide id lives in the
 * URL path, which stays stable across the reload loop but changes on navigation.
 */
function currentSequenceKey(): string {
  try {
    return window.location.pathname;
  } catch {
    return '';
  }
}

function getNavigationType(): string {
  try {
    const [entry] = performance.getEntriesByType(
      'navigation'
    ) as PerformanceNavigationTiming[];
    return entry?.type ?? 'navigate';
  } catch {
    return 'navigate';
  }
}

/**
 * Records, in sessionStorage, that the app itself is about to trigger a reload.
 * Read back on the next boot by `recordReloadProvenanceOnBoot` to tell
 * app-initiated reloads apart from external ones.
 */
export function markAppReload(
  reason: string,
  extra: Record<string, unknown> = {}
): void {
  sessionSet(
    APP_RELOAD_REASON_KEY,
    JSON.stringify({
      reason,
      at: Date.now(),
      url: window.location.href,
      ...extra
    })
  );
}

/**
 * Anti-speedhack handler. Reports the abnormal playback rate to Sentry once per
 * sequence/slide, waits for the event to flush, then reloads — preserving the
 * original reload behavior while avoiding a Sentry flood during the loop.
 */
export function reportSpeedHackAndReload(
  playbackRate: number,
  source: 'audio' | 'video'
): void {
  if (handlingSpeedHack) return;
  handlingSpeedHack = true;

  markAppReload('speed-hack', { playbackRate, source });

  const reload = () => {
    try {
      window.location.reload();
    } catch {
      // ignore
    }
  };

  // Only the first detection for this slide sends to Sentry; the rest just reload.
  const reportedKey = SPEED_REPORTED_PREFIX + currentSequenceKey();
  if (sessionGet(reportedKey)) {
    reload();
    return;
  }
  sessionSet(reportedKey, '1');

  try {
    Sentry.captureMessage(
      'Anti-speedhack: playbackRate > 2 detected, reloading page',
      {
        level: 'warning',
        tags: {
          feature: 'anti-speedhack',
          source,
          inIframe: String(isInIframe())
        },
        extra: {
          playbackRate,
          navigationType: getNavigationType(),
          url: window.location.href,
          referrer: document.referrer,
          userAgent: navigator.userAgent
        }
      }
    );
    // Ensure the event leaves the browser before we tear the page down. The
    // 2s cap keeps the UX from hanging if the network is slow/unavailable.
    Sentry.flush(2000).then(reload, reload);
  } catch {
    reload();
  }
}

/**
 * Call once on app boot. If the current page load is a reload that the app did
 * NOT initiate (no marker in sessionStorage), that is a strong signal something
 * outside our app caused it (e.g. the host LMS reloading our iframe). Reported
 * to Sentry once per sequence/slide.
 */
export function recordReloadProvenanceOnBoot(): void {
  const reasonRaw = sessionGet(APP_RELOAD_REASON_KEY);

  if (reasonRaw) {
    // The previous reload was ours — keep it as a breadcrumb and clear the flag.
    sessionRemove(APP_RELOAD_REASON_KEY);
    try {
      Sentry.addBreadcrumb({
        category: 'reload',
        level: 'info',
        message: 'app-initiated reload',
        data: JSON.parse(reasonRaw) as Record<string, unknown>
      });
    } catch {
      // ignore parse / SDK errors
    }
    return;
  }

  if (getNavigationType() !== 'reload') return;

  // Page was reloaded, but the app did not initiate it.
  const externalKey = EXTERNAL_RELOAD_REPORTED_PREFIX + currentSequenceKey();
  if (sessionGet(externalKey)) return;
  sessionSet(externalKey, '1');

  try {
    Sentry.captureMessage(
      'Page reloaded without an app-initiated reason (possible external/parent-frame reload)',
      {
        level: 'warning',
        tags: {
          feature: 'reload-provenance',
          inIframe: String(isInIframe())
        },
        extra: {
          navigationType: 'reload',
          url: window.location.href,
          referrer: document.referrer,
          userAgent: navigator.userAgent
        }
      }
    );
  } catch {
    // ignore
  }
}
